import { CloseOutlined } from '@ant-design/icons'
import {
  Button,
  Col,
  Drawer,
  Form,
  FormInstance,
  InputNumber,
  Row,
  Space,
} from 'antd'
import { DefaultPatientThreshold } from '../../constants/defaultThreshold'
import { FooterDiv } from './NewPatientViewStyle'

type Props = {
  open: boolean
  onClose: () => void
  form: FormInstance<any>
  onFinish: () => void
}

function ConfigureThresholdView({ form, onClose, onFinish, open }: Props) {
  const setDefaultThresholdEvent = () => {
    form.setFieldsValue(DefaultPatientThreshold)
  }

  return (
    <Drawer
      title={`Configure Patient Threshold`}
      width='50vw'
      onClose={onClose}
      closeIcon={false}
      open={open}
      extra={
        <Space>
          <Button shape='circle' type='text' onClick={onClose}>
            <CloseOutlined />
          </Button>
        </Space>
      }
    >
      <Form
        layout='vertical'
        form={form}
        onFinish={() => {
          onFinish()
        }}
      >
        {/* SPO2 */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name='spo2_max_val'
              label='Maximum Spo2 Value'
              rules={[
                { required: true, message: 'Please enter Maximum Spo2 Value' },
                { validator: (_, value) => validateMax999(value) },
              ]}
            >
              <InputNumber max={999} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='spo2_min_val'
              label='Minimum Spo2 Value'
              rules={[
                { required: true, message: 'Please enter Minimum Spo2 Value' },
                { validator: (_, value) => validateMax999(value) },
              ]}
            >
              <InputNumber max={999} />
            </Form.Item>
          </Col>
        </Row>

        {/* PULSE RATE */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name='pr_max_val'
              label='Maximum Pulse Rate Value'
              rules={[
                {
                  required: true,
                  message: 'Please enter Maximum Pulse Rate Value',
                },
                { validator: (_, value) => validateMax999(value) },
              ]}
            >
              <InputNumber max={999} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='pr_min_val'
              label='Minimum Pulse Rate Value'
              rules={[
                {
                  required: true,
                  message: 'Please enter Minimum Pulse Rate Value',
                },
                { validator: (_, value) => validateMax999(value) },
              ]}
            >
              <InputNumber max={999} />
            </Form.Item>
          </Col>
        </Row>

        {/* RESPIRATION RATE */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name='resp_max_val'
              label='Maximum Respiration Rate Value'
              rules={[
                {
                  required: true,
                  message: 'Please enter Maximum Respiration Rate Value',
                },
                { validator: (_, value) => validateMax999(value) },
              ]}
            >
              <InputNumber max={999} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='resp_min_val'
              label='Minimum Respiration Rate Value'
              rules={[
                {
                  required: true,
                  message: 'Please enter Minimum Respiration Rate Value',
                },
                { validator: (_, value) => validateMax999(value) },
              ]}
            >
              <InputNumber max={999} />
            </Form.Item>
          </Col>
        </Row>

        {/* TEMPERATURE */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name='temp_max_val'
              label='Maximum Temperature Value'
              rules={[
                {
                  required: true,
                  message: 'Please enter Maximum Temperature Value',
                },
                { validator: (_, value) => validateMax99Point9(value) },
              ]}
            >
              <InputNumber max={99.9} precision={1} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='temp_min_val'
              label='Minimum Temperature Value'
              rules={[
                {
                  required: true,
                  message: 'Please enter Minimum Temperature Value',
                },
                { validator: (_, value) => validateMax99Point9(value) },
              ]}
            >
              <InputNumber max={99.9} precision={1} />
            </Form.Item>
          </Col>
        </Row>

        {/* SYSTOLIC PRESSURE */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name='sys_max_val'
              label='Maximum Systolic Blood Pressure Value'
              rules={[
                {
                  required: true,
                  message: 'Please enter Maximum Systolic Blood Pressure Value',
                },
                { validator: (_, value) => validateMax999(value) },
              ]}
            >
              <InputNumber max={999} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='sys_min_val'
              label='Minimum Systolic Blood Pressure Value'
              rules={[
                {
                  required: true,
                  message: 'Please enter Minimum Systolic Blood Pressure Value',
                },
                { validator: (_, value) => validateMax999(value) },
              ]}
            >
              <InputNumber max={999} />
            </Form.Item>
          </Col>
        </Row>

        {/* DIASTOLIC PRESSURE */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name='dia_max_val'
              label='Maximum Diastolic Blood Pressure Value'
              rules={[
                {
                  required: true,
                  message:
                    'Please enter Maximum Diastolic Blood Pressure Value',
                },
                { validator: (_, value) => validateMax999(value) },
              ]}
            >
              <InputNumber max={999} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='dia_min_val'
              label='Minimum Diastolic Blood Pressure Value'
              rules={[
                {
                  required: true,
                  message:
                    'Please enter Minimum Diastolic Blood Pressure Value',
                },
                { validator: (_, value) => validateMax999(value) },
              ]}
            >
              <InputNumber max={999} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <FooterDiv>
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={() => form.submit()} type='primary'>
            Update Threshold
          </Button>
          <Button onClick={setDefaultThresholdEvent} type='primary'>
            Set Default Threshold
          </Button>
        </Space>
      </FooterDiv>
    </Drawer>
  )
}

export default ConfigureThresholdView

function validateMax999(value: any) {
  if (value === undefined || value === null) {
    return Promise.reject(new Error(''))
  }
  if (value > 999) {
    return Promise.reject(new Error('Value cannot exceed 999'))
  }
  return Promise.resolve()
}

function validateMax99Point9(value: any) {
  if (value === undefined || value === null) {
    return Promise.reject(new Error(''))
  }
  if (value > 99.9) {
    return Promise.reject(new Error('Value cannot exceed 99.9'))
  }
  return Promise.resolve()
}
